import React, { useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Heading,
  Spinner,
  Center,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { fetchAdminNps } from "../../actions/user/userActions";


const ADMIN_EMAIL = "tech@sylvr.io";

const getBucket = (score) => {
  if (typeof score !== "number") return null;
  if (score <= 6) return { label: "Detractor", color: "red" };
  if (score <= 8) return { label: "Passive", color: "orange" };
  return { label: "Promoter", color: "green" };
};

export default function NpsAdmin() {
  const dispatch = useDispatch();

  // ✅ AUTH ALWAYS COMES FROM userReducer
  const user = useSelector((state) => state.userReducer?.data);

  // ✅ ADMIN DATA FROM npsAdminReducer
  const { adminRows = [], adminLoading } = useSelector(
    (state) => state.npsAdminReducer
  );

  const bg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");

  // ✅ Fetch admin NPS only if admin
  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      dispatch(fetchAdminNps());
    }
  }, [dispatch, user]);

  /* ===============================
     🔐 AUTH GUARD
  =============================== */
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.email !== ADMIN_EMAIL) {
    return <Navigate to="/unauthorized" replace />;
  }

  /* ===============================
     LOADING
  =============================== */
  if (adminLoading) {
    return (
      <Center minH="300px">
        <Spinner size="lg" color="teal.400" />
      </Center>
    );
  }

  /* ===============================
     UI
  =============================== */
  return (
    <Box
      maxW="1200px"
      mx="auto"
      mt={8}
      p={6}
      bg={bg}
      borderRadius="lg"
      border="1px solid"
      borderColor={border}
    >
      <Heading size="md" mb={4} color="teal.500">
        NPS Admin Dashboard
      </Heading>

      <Table variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th>ID</Th>
            <Th>Email</Th>
            <Th>Company Name</Th>
            <Th>Company Sector</Th>
            <Th textAlign="center">NPS Score</Th>
            <Th>Updated Date</Th>
          </Tr>
        </Thead>

        <Tbody>
          {adminRows.length === 0 ? (
            <Tr>
              <Td colSpan={6}>
                <Text textAlign="center" color="gray.500">
                  No NPS records found
                </Text>
              </Td>
            </Tr>
          ) : (
            adminRows.map((row) => {
              const bucket = getBucket(row.mainScore);

              return (
                <Tr key={row.id}>
                  <Td fontWeight="600">{row.id}</Td>
                  <Td>{row.email || "-"}</Td>
                  <Td>{row.companyName || "-"}</Td>
                  <Td>{row.companySector || "-"}</Td>
                  <Td textAlign="center">
                    {bucket ? (
                      <Badge colorScheme={bucket.color} px={3} py={1}>
                        {row.mainScore} · {bucket.label}
                      </Badge>
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td>
                    {row.updatedAt
                      ? new Date(row.updatedAt).toLocaleDateString("en-IN", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : "-"}
                  </Td>
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </Box>
  );
}
